import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Trash2, Database, RefreshCw } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const ClearCachePage = () => {
  const { toast } = useToast();

  const clearAllStorage = () => {
    try {
      // Clear localStorage
      localStorage.clear();
      
      // Clear sessionStorage
      sessionStorage.clear();
      
      // Clear any cached API data
      if ('caches' in window) {
        caches.keys().then(names => {
          names.forEach(name => {
            caches.delete(name);
          });
        });
      }
      
      toast({
        title: 'Success!',
        description: 'All cached data has been cleared. Please refresh the page.',
      });
      
      // Auto-refresh after 2 seconds
      setTimeout(() => {
        window.location.href = '/';
      }, 2000);
      
    } catch (error) {
      console.error('Error clearing cache:', error);
      toast({
        title: 'Error',
        description: 'Failed to clear some cached data. Please try manually refreshing.',
        variant: 'destructive'
      });
    }
  };

  const clearChildData = () => {
    try {
      // Clear child-specific data
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.includes('child') || key.includes('Child') || key.startsWith('child_photo_')) {
          localStorage.removeItem(key);
        }
      });
      
      toast({
        title: 'Child Data Cleared',
        description: 'Child-specific cached data has been cleared.',
      });
      
    } catch (error) {
      console.error('Error clearing child data:', error);
      toast({
        title: 'Error',
        description: 'Failed to clear child data.',
        variant: 'destructive'
      });
    }
  };

  const clearAssignmentCache = () => {
    try {
      // Clear assignment-specific cache
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.includes('assignment') || key.includes('Assignment')) {
          localStorage.removeItem(key);
        }
      });
      
      toast({
        title: 'Assignment Cache Cleared',
        description: 'Assignment cache has been cleared.',
      });
      
    } catch (error) {
      console.error('Error clearing assignment cache:', error);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-slate-900">Clear Cache & Data</h1>
          <p className="text-slate-600 mt-2">
            Use these tools to clear cached data and fix display issues.
          </p>
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trash2 className="w-5 h-5" />
                Clear All Data
              </CardTitle>
              <CardDescription>
                Clears all cached data including login tokens, assignments, and preferences. 
                You'll need to log in again.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={clearAllStorage} className="bg-red-600 hover:bg-red-700 text-white">
                Clear Everything & Refresh
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="w-5 h-5" />
                Clear Child Data
              </CardTitle>
              <CardDescription>
                Clears child login tokens, profile photos, and child-specific cache.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={clearChildData} variant="outline">
                Clear Child Data
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <RefreshCw className="w-5 h-5" />
                Clear Assignment Cache
              </CardTitle>
              <CardDescription>
                Clears cached assignment data to fix old assignments showing up.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={clearAssignmentCache} variant="outline">
                Clear Assignment Cache
              </Button>
            </CardContent>
          </Card>
        </div>

        <div className="mt-8 p-4 bg-blue-50 rounded-lg">
          <h3 className="font-semibold text-blue-900">Current Storage:</h3>
          <div className="mt-2 text-sm text-blue-800">
            <p>localStorage items: {Object.keys(localStorage).length}</p>
            <p>sessionStorage items: {Object.keys(sessionStorage).length}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClearCachePage;